import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, useLocation, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Button, EmptyState, Skeleton } from '@/components';
import {
  WorkspaceCalendarPage,
  type WorkspaceCalendarListOption,
} from '@/components/workspace/WorkspaceCalendarPage';
import { workspaceBoardsQueryOptions } from '@/lib/boards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { boardLabelsQueryOptions } from '@/lib/labels';
import { boardListsQueryOptions } from '@/lib/lists';
import { weekStartForLocale } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import { signInHref, useSession } from '@/lib/session';
import {
  cappedBoardSelection,
  nextCalendarMonth,
  previousCalendarMonth,
  selectedBoardIdsFromSearch,
  useWorkspaceCalendar,
  workspaceCalendarFilterFromSearch,
  workspaceCalendarFilterSearchParams,
  workspaceCalendarMonth,
} from '@/lib/workspace-calendar';
import { workspaceMembersQueryOptions, workspaceQueryOptions } from '@/lib/workspaces';

interface WorkspaceCalendarSearch {
  readonly month?: string | undefined;
  readonly boardIds?: readonly string[] | undefined;
  readonly members?: readonly string[] | undefined;
  readonly labels?: readonly string[] | undefined;
  readonly lists?: readonly string[] | undefined;
  readonly due?: readonly string[] | undefined;
  readonly keyword?: string | undefined;
}

export const Route = createFileRoute('/w/$workspaceId/calendar')({
  validateSearch: (search: Record<string, unknown>): WorkspaceCalendarSearch => {
    const month = monthFromSearch(search.month);
    const boardIds = selectedBoardIdsFromSearch(search.boardIds);
    return {
      ...(month === undefined ? {} : { month }),
      ...(boardIds.length === 0 ? {} : { boardIds }),
      ...workspaceCalendarSearchFromRecord(search),
    };
  },
  component: WorkspaceCalendarRoute,
});

function WorkspaceCalendarRoute() {
  const { t } = useInterfaceLanguage();
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const router = useRouter();
  const rawSearch = router.history.location.search;
  const search = Route.useSearch();
  const {
    profile,
    isPending: sessionPending,
    isSignedOut,
    error: sessionError,
  } = useSession();

  const canFetch = !isSignedOut && !sessionPending && profile !== null;
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const boards = useQuery({
    ...workspaceBoardsQueryOptions(workspaceId, 'open'),
    enabled: canFetch,
  });
  const members = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const { language } = useLocalePreferencesValue();
  const month = workspaceCalendarMonth(search.month, weekStartForLocale(language));
  const rawParams = new URLSearchParams(rawSearch);
  const rawBoardIds = selectedBoardIdsFromSearch(rawParams.getAll('boardIds'));
  const explicitBoardIds = rawParams.has('boardIds')
    ? rawBoardIds.length === 0
      ? (search.boardIds ?? [])
      : rawBoardIds
    : null;
  const selectedBoardIds =
    explicitBoardIds === null
      ? cappedBoardSelection((boards.data ?? []).map((board) => board.id))
      : cappedBoardSelection(explicitBoardIds);
  const filter = workspaceCalendarFilterFromSearch(rawSearch);
  const calendar = useWorkspaceCalendar(
    {
      workspaceId,
      boardIds: selectedBoardIds,
      from: month.from,
      to: month.to,
      filter,
    },
    canFetch && selectedBoardIds.length > 0,
  );
  const selectedBoards = (boards.data ?? []).filter((board) =>
    selectedBoardIds.includes(board.id),
  );
  const labelQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardLabelsQueryOptions(board.id),
      enabled: canFetch,
    })),
  });
  const listQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardListsQueryOptions(board.id, 'open'),
      enabled: canFetch,
    })),
  });
  const labels = dedupeById(labelQueries.flatMap((query) => query.data ?? []));
  const lists = selectedBoards.flatMap<WorkspaceCalendarListOption>((board, index) =>
    (listQueries[index]?.data ?? []).map((list) => ({
      ...list,
      boardTitle: board.title,
    })),
  );
  const loadingFilters =
    members.isPending ||
    labelQueries.some((query) => query.isPending) ||
    listQueries.some((query) => query.isPending);

  function replaceCalendarSearch(next: {
    readonly month: string;
    readonly boardIds: readonly string[];
    readonly filter: ReturnType<typeof workspaceCalendarFilterFromSearch>;
  }): void {
    const params = new URLSearchParams();
    params.set('month', next.month);
    for (const boardId of next.boardIds) params.append('boardIds', boardId);
    for (const [key, value] of workspaceCalendarFilterSearchParams(next.filter)) {
      params.append(key, value);
    }
    router.history.replace(`${location.pathname}?${params.toString()}`);
    router.history.flush();
  }

  function handleBoardSelectionChange(boardIds: readonly string[]): void {
    replaceCalendarSearch({
      month: month.month,
      boardIds: cappedBoardSelection(boardIds),
      filter,
    });
  }

  function handleFilterChange(
    nextFilter: ReturnType<typeof workspaceCalendarFilterFromSearch>,
  ): void {
    replaceCalendarSearch({
      month: month.month,
      boardIds: selectedBoardIds,
      filter: nextFilter,
    });
  }

  function handlePreviousMonth(): void {
    replaceCalendarSearch({
      month: previousCalendarMonth(month.month),
      boardIds: selectedBoardIds,
      filter,
    });
  }

  function handleNextMonth(): void {
    replaceCalendarSearch({
      month: nextCalendarMonth(month.month),
      boardIds: selectedBoardIds,
      filter,
    });
  }

  if (isSignedOut) {
    return (
      <WorkspaceCalendarFrame title={t('workspaceViews.calendar.title')}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.calendar.signInTitle')}
          body={t('workspaceViews.calendar.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({
                pathname: `/w/${workspaceId}/calendar`,
                search: '',
              })}
            >
              {t('workspaceViews.calendar.signInAction')}
            </Button>
          }
        />
      </WorkspaceCalendarFrame>
    );
  }

  if (sessionPending) {
    return (
      <WorkspaceCalendarFrame title={t('workspaceViews.calendar.title')}>
        <WorkspaceCalendarPending />
      </WorkspaceCalendarFrame>
    );
  }

  if (profile === null) {
    return (
      <WorkspaceCalendarFrame title={t('workspaceViews.calendar.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.calendar.accountErrorTitle')}
          body={sessionError?.message ?? t('workspaceViews.calendar.genericError')}
        />
      </WorkspaceCalendarFrame>
    );
  }

  const loadError = workspace.error ?? boards.error;
  if (loadError !== null) {
    const notFound = loadError.code === 'not_found';
    return (
      <WorkspaceCalendarFrame title={t('workspaceViews.calendar.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            /* ⚠ EM DASH, matching the table route. */
            notFound
              ? t('workspaceViews.calendar.notFound')
              : t('workspaceViews.calendar.loadFailedTitle')
          }
          {...(notFound ? {} : { body: loadError.message })}
        />
      </WorkspaceCalendarFrame>
    );
  }

  const workspaceName =
    workspace.data?.name !== undefined && workspace.data.name.trim() !== ''
      ? workspace.data.name
      : t('workspaceViews.calendar.workspaceFallback');

  return (
    <WorkspaceCalendarPage
      workspaceName={workspaceName}
      boards={boards.data ?? []}
      selectedBoardIds={selectedBoardIds}
      filter={filter}
      members={members.data ?? []}
      labels={labels}
      lists={lists}
      calendar={calendar.data}
      month={month}
      loadingBoards={boards.isPending}
      loadingFilters={loadingFilters}
      loadingCalendar={calendar.isPending && selectedBoardIds.length > 0}
      error={calendar.error?.message}
      onBoardSelectionChange={handleBoardSelectionChange}
      onFilterChange={handleFilterChange}
      onPreviousMonth={handlePreviousMonth}
      onNextMonth={handleNextMonth}
      onRetry={() => {
        void workspace.refetch();
        void boards.refetch();
        void members.refetch();
        for (const query of labelQueries) void query.refetch();
        for (const query of listQueries) void query.refetch();
        void calendar.refetch();
      }}
    />
  );
}

function WorkspaceCalendarFrame({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
        {title}
      </h1>
      {children}
    </div>
  );
}

function WorkspaceCalendarPending() {
  return (
    <div aria-busy="true" className="flex flex-col gap-4">
      <Skeleton shape="block" count={3} />
    </div>
  );
}

function monthFromSearch(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : undefined;
}

function workspaceCalendarSearchFromRecord(
  search: Record<string, unknown>,
): Pick<WorkspaceCalendarSearch, 'members' | 'labels' | 'lists' | 'due' | 'keyword'> {
  const filter = workspaceCalendarFilterFromSearch(search);
  return {
    ...(filter.members.length === 0 ? {} : { members: filter.members }),
    ...(filter.labels.length === 0 ? {} : { labels: filter.labels }),
    ...(filter.lists.length === 0 ? {} : { lists: filter.lists }),
    ...(filter.due.length === 0 ? {} : { due: filter.due }),
    ...(filter.keyword === '' ? {} : { keyword: filter.keyword }),
  };
}

function dedupeById<T extends { readonly id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }
  return deduped;
}
